import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAsyncGetVendorExpenseDetails } from "redux/vendorSlice";
import ExpenseTable from "./expenseTable";
import { poNumberFormate } from "jsFunctions/helperFunctions";
import { currencyFormate } from "jsFunctions/helperFunctions";
import dateTimeFormat from "jsFunctions/dateTimeFormat";
import {
  expenseDataColumns,
  gstDataColumns,
  tdsDataColumns,
} from "./variable/variable";
import { FaEllipsisV } from "react-icons/fa";
import { useToasts } from "react-toast-notifications";
import * as FileSaver from "file-saver";
import XLXS from "sheetjs-style";
import { getVendorExpenseExcelData } from "components/exportData/excelData/vendorExpenseExcelData";
import moment from "moment";
import { getVendorTdsExcelData } from "components/exportData/excelData/vendorTdsExcel";
import { getVendorGstExcelData } from "components/exportData/excelData/vendorGstExcel";
import SpinnerLoader from "components/Spinner/SpinnerLoader";
import { formatAmountIndianRupees } from "jsFunctions/formatAmountInINR";
import { generateYearOptions } from "../default/components/filterOption";

const { yearOption, defaultSelected } = generateYearOptions();
const ExpenseDetails = ({ activeTab }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const currUser = useSelector((state) => state.auth.currUser);
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false); // Loading state

  const [role, setRole] = useState("");

  const [tdsColData, setTdsColData] = useState([]);
  const [gstColData, setGstColData] = useState([]);
  const [vendor, setVendor] = useState({});

  const [expenseSummary, setExpenseSummary] = useState({});

  const [selectedYear, setSelectedYear] = useState(defaultSelected);

  const [expenseDetails, setExpenseDetails] = useState({
    allExpenses: [],
    tdsData: [],
    gstData: [],
  });

  useEffect(() => {
    if (currUser && currUser?.auth) {
      const auth = currUser?.auth;
      const roleObj =
        auth &&
        auth?.length > 0 &&
        auth.find((item) => item?.module === "Vendor");
      if (roleObj) {
        setRole(roleObj?.access);
      }
    }
  }, [currUser]);

  const fetchExpenseDetails = async (expenseId) => {
    setLoading(true);
    try {
      const res = await dispatch(
        fetchAsyncGetVendorExpenseDetails({
          id: expenseId,
          financialYear: selectedYear,
        }),
      );
      if (res?.payload) {
        setVendor(res?.payload?.vendor);

        setExpenseDetails({
          allExpenses: res?.payload?.expense?.expenseData || [],
          tdsData: res?.payload?.tds?.expenseData || [],
          gstData: res?.payload?.gst?.expenseData || [],
        });
        const summary = {
          totalExpense: res?.payload?.expense?.total || 0,
          totalTds: res?.payload?.tds?.total || 0,
          totalGst: res?.payload?.gst?.total || 0,
        };
        setExpenseSummary(summary);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);

      console.error("Error fetching expense details:", error);
    }
  };

  useEffect(() => {
    if (id && activeTab === "expenses") {
      fetchExpenseDetails(id);
    }
  }, [id, selectedYear, activeTab]);

  const expenseColData = useMemo(() => {
    return expenseDetails.allExpenses.map((data, index) => ({
      _id: data._id,
      no: index + 1,
      poNo: (
        <span style={{ cursor: "pointer", color: "blue" }}>
          {data.poNo && poNumberFormate(data.poNo)}
        </span>
      ),
      purchasedate: dateTimeFormat(data.oDt),
      category: <span>{data?.cat}</span>,
      majorType: <span style={{ cursor: "pointer" }}>{data?.mjId?.val}</span>,
      minorType: <span style={{ cursor: "pointer" }}>{data?.mnId?.val}</span>,
      sortApdAmt: Number(data?.pyInfo?.tlAmt || 0),
      approvedAmount: (
        <span style={{ cursor: "pointer" }} className="creditAmount blue">
          {data.pyInfo?.tlAmt ? (
            currencyFormate(data.pyInfo?.tlAmt)
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
              }}
            />
          )}
        </span>
      ),
    }));
  }, [expenseDetails.allExpenses]);

  useEffect(() => {
    if (expenseDetails && expenseDetails?.tdsData?.length > 0) {
      const tableData = expenseDetails?.tdsData.map((data, index) => {
        return {
          _id: data._id,
          no: index + 1,
          poNo: (
            <span style={{ cursor: "pointer", color: "blue" }}>
              {data.poNo && poNumberFormate(data.poNo)}
            </span>
          ),
          orderdate: dateTimeFormat(data.oDt),
          resourcename: (
            <span>
              {data?.rcId && data?.rcId?.name ? data?.rcId?.name : ""}
            </span>
          ),
          sortTlAmt: Number(data?.tlBlAmt || 0),
          totalAmount: data?.tlBlAmt ? (
            currencyFormate(data?.tlBlAmt)
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
              }}
            />
          ),
          sortApdAmt: Number(data?.pyInfo?.tlAmt || 0),
          approvedAmount: (
            <span style={{ cursor: "pointer" }} className="creditAmount blue">
              {data.pyInfo?.tlAmt ? (
                currencyFormate(data.pyInfo?.tlAmt)
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
                  }}
                />
              )}
            </span>
          ),
        };
      });
      setTdsColData(tableData);
    } else {
      setTdsColData([]);
    }
  }, [expenseDetails, expenseDetails.tdsData]);

  useEffect(() => {
    if (expenseDetails && expenseDetails?.gstData?.length > 0) {
      const tableData = expenseDetails?.gstData.map((data, index) => {
        const totalGst =
          Number(data?.amt?.i || 0) +
          Number(data?.amt?.c || 0) +
          Number(data?.amt?.s || 0);
        return {
          _id: data._id,
          no: index + 1,
          poNo: (
            <span style={{ cursor: "pointer", color: "blue" }}>
              {data?.poNo && poNumberFormate(data?.poNo)}
            </span>
          ),
          sortBaseAmt: Number(data?.bsAmt || 0),
          baseAmount: (
            <span>
              {data?.bsAmt ? (
                currencyFormate(data?.bsAmt)
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
                  }}
                />
              )}
            </span>
          ),
          sortIgstAmt: Number(data?.amt?.i || 0),
          igstAmt: (
            <span>
              {data?.amt?.i ? (
                currencyFormate(data?.amt?.i)
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
                  }}
                />
              )}
            </span>
          ),
          sortSgstAmt: Number(data?.amt?.s || 0),
          sgstAmt: (
            <span>
              {data?.amt?.s ? (
                currencyFormate(data?.amt?.s)
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
                  }}
                />
              )}
            </span>
          ),
          sortCgstAmt: Number(data?.amt?.c || 0),
          cgstAmt: (
            <span>
              {data?.amt?.c ? (
                currencyFormate(data?.amt?.c)
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
                  }}
                />
              )}
            </span>
          ),
          sortTlAmt: Number(totalGst || 0),
          totalGst: (
            <span className="creditAmount blue">
              {totalGst ? (
                currencyFormate(totalGst)
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<span style="font-family: sans-serif">₹</span> ${Number(0).toFixed(2)}`,
                  }}
                />
              )}
            </span>
          ),

          date: dateTimeFormat(data.oDt),
        };
      });
      setGstColData(tableData);
    } else {
      setGstColData([]);
    }
  }, [expenseDetails, expenseDetails.gstData]);

  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const formatFilename = (filename) => {
    return `${filename}${moment().format("DD-MMM-YYYY")}`;
  };
  const handleExportExpense = async () => {
    if (!expenseDetails || !expenseDetails?.allExpenses?.length > 0) {
      addToast(`No Expense data available `, { appearance: "error" });
      return;
    }
    const expenseData = expenseDetails?.allExpenses;
    const excelData = await getVendorExpenseExcelData(expenseData);
    const ws = XLXS.utils.json_to_sheet(excelData);
    const columnCount = Object.keys(excelData[0]).length;
    const columnWidths = Array(columnCount).fill({ wch: 15 });
    ws["!cols"] = columnWidths;
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLXS.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    const filename = `${vendor?.name}_expenses_`;
    FileSaver.saveAs(data, formatFilename(filename) + fileExtension);
  };

  const handleExportTds = async () => {
    if (!expenseDetails || !expenseDetails?.tdsData?.length > 0) {
      addToast(`No Expense data available `, { appearance: "error" });
      return;
    }
    const expenseData = expenseDetails?.tdsData;
    const excelData = await getVendorTdsExcelData(expenseData);
    const ws = XLXS.utils.json_to_sheet(excelData);
    const columnCount = Object.keys(excelData[0]).length;
    const columnWidths = Array(columnCount).fill({ wch: 15 });
    ws["!cols"] = columnWidths;
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLXS.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    const filename = `${vendor?.name}_TDS_`;
    FileSaver.saveAs(data, formatFilename(filename) + fileExtension);
  };

  const handleExportGst = async () => {
    if (!expenseDetails || !expenseDetails?.gstData?.length > 0) {
      addToast(`No Expense data available `, { appearance: "error" });
      return;
    }
    const expenseData = expenseDetails?.gstData;
    const excelData = await getVendorGstExcelData(expenseData);
    const ws = XLXS.utils.json_to_sheet(excelData);
    const columnCount = Object.keys(excelData[0]).length;
    const columnWidths = Array(columnCount).fill({ wch: 15 });
    ws["!cols"] = columnWidths;
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLXS.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    const filename = `${vendor?.name}_GST_`;
    FileSaver.saveAs(data, formatFilename(filename) + fileExtension);
  };

  if (loading) {
    return <SpinnerLoader />;
  }

  return (
    <Box display="flex" className="mx-0 expense_details">
      <Box className="expense_details_main">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="20px" mt={5}>
            Expense Details
          </Text>
          <Box display="flex">
            <Select
              className="graph-year-select"
              width="auto"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {yearOption.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </Select>
            <Menu>
              <MenuButton
                p="0px"
                className="export_vendor"
                as={Button}
                rightIcon={<FaEllipsisV />}
              ></MenuButton>
              <MenuList className="vendor-menu-list">
                <MenuItem
                  isDisabled={!expenseDetails?.allExpenses?.length}
                  onClick={(e) => handleExportExpense(e)}
                >
                  Export Expense
                </MenuItem>
                <MenuItem
                  isDisabled={!expenseDetails?.tdsData?.length}
                  onClick={(e) => handleExportTds(e)}
                >
                  Export TDS
                </MenuItem>
                <MenuItem
                  isDisabled={!expenseDetails?.gstData?.length}
                  onClick={(e) => handleExportGst(e)}
                >
                  Export GST
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>
        </Box>

        <Box className="acordian-box mt-1 ">
          <Accordion defaultIndex={[0]} allowMultiple>
            {/* All Expense Data Accordion */}
            <AccordionItem className="acordian-item" borderTop="none">
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="600">Expenses </Text>
                  </Box>
                  <Box
                    flex="1"
                    className="mx-3"
                    display="flex"
                    justifyContent="flex-end"
                    flexDirection="row"
                  >
                    <Text className="mt-1" fontWeight="400">
                      Total Expense :{" "}
                      <span className="creditAmount blue">
                        ₹{" "}
                        {expenseSummary?.totalExpense
                          ? formatAmountIndianRupees(
                              expenseSummary?.totalExpense,
                            )
                          : Number(0).toFixed(2)}
                      </span>{" "}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel className="acordian-panal  px-2 p-0 m-0">
                {expenseColData && expenseColData?.length > 0 ? (
                  <ExpenseTable
                    columnsData={expenseDataColumns}
                    tableData={expenseColData}
                    role={role}
                  />
                ) : (
                  <Text className="py-3" fontWeight="600" textAlign="center">
                    There are no Expenses
                  </Text>
                )}
              </AccordionPanel>
            </AccordionItem>

            {/* TDS Data Accordion */}
            <AccordionItem className="acordian-item" borderTop="none">
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="600">TDS</Text>
                  </Box>
                  <Box
                    flex="1"
                    className="mx-3"
                    display="flex"
                    justifyContent="right"
                    flexDirection="row"
                  >
                    <Text className="mt-1" fontWeight="400">
                      Total TDS:{" "}
                      <span className="creditAmount blue">
                        ₹{" "}
                        {expenseSummary?.totalTds
                          ? formatAmountIndianRupees(expenseSummary?.totalTds)
                          : Number(0).toFixed(2)}
                      </span>{" "}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel className="acordian-panal px-2 p-0 m-0">
                {tdsColData && tdsColData?.length > 0 ? (
                  <ExpenseTable
                    columnsData={tdsDataColumns}
                    tableData={tdsColData}
                    role={role}
                  />
                ) : (
                  <Text className="py-3" fontWeight="600" textAlign="center">
                    There are no TDS
                  </Text>
                )}
              </AccordionPanel>
            </AccordionItem>

            {/* GST Data Accordion */}
            <AccordionItem className="acordian-item" borderTop="none">
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="600">GST</Text>
                  </Box>
                  <Box
                    flex="1"
                    className="mx-3"
                    display="flex"
                    justifyContent="right"
                    flexDirection="row"
                  >
                    <Text className="mt-1" fontWeight="400">
                      Total GST:{" "}
                      <span className="creditAmount blue">
                        ₹{" "}
                        {expenseSummary?.totalGst
                          ? formatAmountIndianRupees(expenseSummary?.totalGst)
                          : Number(0).toFixed(2)}
                      </span>{" "}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel className="acordian-panal px-2 p-0 m-0">
                {expenseDetails.gstData.length > 0 ? (
                  <ExpenseTable
                    columnsData={gstDataColumns}
                    tableData={gstColData}
                    role={role}
                  />
                ) : (
                  <Text className="py-3" fontWeight="600" textAlign="center">
                    There are no GST
                  </Text>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpenseDetails;
